import { useEffect, useState } from 'react';
import { useLocation, useParams } from 'react-router';
import { PortadaEstudio } from '../../../common/components/PortadaEstudio.js';
import { DatoEnlazado, filtroDelEnlace } from '../components/DatoEnlazado.js';
import { pedirFicha } from '../../../common/services/catalogo.js';
import { diccionarioDe, type Idioma } from '../../../i18n/index.js';
import { contextoEnIdioma } from '../contextoIdioma.js';
import { enProsa, idiomaDeLaCitaDelEditor, presentarTransparencia, type Ficha, type NombreHechoTransparencia } from '../ficha.js';
import { ENLACE_A_EDITOR } from '../../../common/utils/enlaces.js';

/**
 * La ficha de un estudio: qué declara.
 *
 * **Sin botón de «Preguntar sobre este estudio»** (Iván, 2026-10-17): rellenaba la caja con «Según el
 * estudio … de …,» y en producción se enviaba así, sin pregunta detrás (2 de 13 preguntas el 2026-10-15).
 *
 * **No lista los datos** (Iván, 2026-09-25): eran cientos en los estudios grandes y se leen mejor
 * en las respuestas, cada uno con su contexto. **Ni los cuenta** (Iván, 2026-10-01): cuántos, de
 * dónde y en qué páginas era información interna; la API la sigue mandando en `estadistica`.
 *
 * Athena **no publica veredictos sobre las fuentes** (`plan/PLAN.md` § 1). Esta pantalla no dice si
 * un estudio es bueno: dice qué declara y qué no, y deja que quien lo lea decida. Un hecho que el
 * documento no declara es información, pero no ocupa una fila cada uno: solo se enseña lo que se
 * sabe y, al final, una línea discreta con lo relevante que no indica (Iván, 2026-10-01).
 */
export function FichaPage({ idioma }: { readonly idioma: Idioma }) {
  const { slug } = useParams();
  const { search, hash } = useLocation();
  const enlazado = filtroDelEnlace(search, hash);
  const t = diccionarioDe(idioma).fuentes;
  const [ficha, setFicha] = useState<Ficha | null>(null);

  useEffect(() => {
    if (slug === undefined) return;
    void pedirFicha(slug).then(f => {
      setFicha(f as Ficha | null);
    });
  }, [slug]);

  if (ficha === null) return null;

  const vista = presentarTransparencia(ficha.fuente);
  const editor = contextoEnIdioma(ficha.fuente.editor, idioma) ?? ficha.fuente.editor;
  const quienPublica = vista.sinPatrocinadorLoPublica === null ? null : contextoEnIdioma(vista.sinPatrocinadorLoPublica, idioma);
  const enMinuscula = (n: NombreHechoTransparencia) => t.transparencia.nombres[n].charAt(0).toLowerCase() + t.transparencia.nombres[n].slice(1);
  const lineas = [
    quienPublica === null ? null : t.transparencia.sinPatrocinador.replace('{quien}', quienPublica),
    vista.noIndica.length === 0 ? null : t.transparencia.noIndica.replace('{lista}', enProsa(vista.noIndica.map(enMinuscula), idioma, 'disjunction')),
    vista.sinRevisar.length === 0 ? null : t.transparencia.sinRevisarAun.replace('{lista}', enProsa(vista.sinRevisar.map(enMinuscula), idioma)),
  ].filter(l => l !== null);

  return (
    <div className='flex grow flex-col'>
      <main className='mx-auto flex w-full max-w-[820px] grow flex-col gap-10 px-6 py-10'>
        <header className='flex flex-col gap-6 sm:flex-row sm:items-start'>
          {ficha.fuente.portadaUrl == null ? null : (
            <div className='w-full max-w-[300px] shrink-0 sm:w-[220px]'>
              <PortadaEstudio url={ficha.fuente.portadaUrl} />
            </div>
          )}
          <div className='min-w-0'>
            <h1 className='m-0 font-(family-name:--serif) text-[32px] leading-tight font-bold'>{ficha.fuente.tituloOriginal}</h1>
            <p className='mt-2 mb-0 font-(family-name:--text) text-[15px] text-(--ink-soft)'>
              {[
                editor,
                ficha.fuente.anio === null ? null : String(ficha.fuente.anio),
                ficha.fuente.pais === null ? null : contextoEnIdioma(ficha.fuente.pais, idioma),
              ]
                .filter(p => p !== null && p !== '')
                .join(' · ')}
            </p>
            {ficha.fuente.urlDocumento === null ? null : (
              <p className='mt-3 mb-0'>
                <a href={ficha.fuente.urlDocumento} {...ENLACE_A_EDITOR} className='font-(family-name:--text) text-[14px] text-(--teal-deep)'>
                  {t.abrirDocumento}
                </a>
              </p>
            )}
          </div>
        </header>

        {/* Quien llega con un enlace a un dato (?hecho= o #p16) lo ve aquí, arriba, con su cita. */}
        {enlazado === null || slug === undefined ? null : (
          <DatoEnlazado slug={slug} filtro={enlazado} urlDocumento={ficha.fuente.urlDocumento} idioma={idioma} />
        )}

        {ficha.fuente.editorAQueSeDedica === null && ficha.fuente.editorPais === null && ficha.fuente.editorWeb === null ? null : (
          <section>
            <h2 className='m-0 mb-4 font-(family-name:--serif) text-[22px] font-bold'>{t.editor.titulo.replace('{editor}', editor)}</h2>
            <ul className='m-0 flex list-none flex-col gap-3 p-0'>
              {ficha.fuente.editorAQueSeDedica === null ? null : (
                <li>
                  <blockquote className='m-0 border-l-2 border-(--line-strong) pl-3 text-[15px] leading-[1.55] text-(--ink) italic'>
                    <q lang={idiomaDeLaCitaDelEditor(ficha.fuente.editorAQueSeDedica.cita)}>{ficha.fuente.editorAQueSeDedica.cita}</q>
                  </blockquote>
                  <a
                    href={ficha.fuente.editorAQueSeDedica.url}
                    {...ENLACE_A_EDITOR}
                    className='mt-1 inline-block font-(family-name:--text) text-[13px] text-(--teal-deep)'
                  >
                    {ficha.fuente.editorAQueSeDedica.url}
                  </a>
                </li>
              )}
              {ficha.fuente.editorPais === null ? null : (
                <li className='font-(family-name:--text) text-[14px] text-(--ink)'>
                  {contextoEnIdioma(ficha.fuente.editorPais.valor, idioma) ?? t.transparencia.sinTraduccion}{' '}
                  <a href={ficha.fuente.editorPais.url} {...ENLACE_A_EDITOR} className='text-[13px] text-(--teal-deep)'>
                    ({ficha.fuente.editorPais.url})
                  </a>
                </li>
              )}
              {ficha.fuente.editorWeb === null ? null : (
                <li>
                  <a href={ficha.fuente.editorWeb.valor} {...ENLACE_A_EDITOR} className='font-(family-name:--text) text-[14px] text-(--teal-deep)'>
                    {t.editor.web}
                  </a>
                </li>
              )}
            </ul>
          </section>
        )}

        <section>
          <h2 className='m-0 mb-4 font-(family-name:--serif) text-[22px] font-bold'>{t.transparencia.titulo}</h2>
          <dl className='m-0 grid grid-cols-1 gap-4 sm:grid-cols-[220px_1fr]'>
            {vista.filas.map(({ nombre, valor, respaldo }) => (
              <div key={nombre} className='contents'>
                <dt className='font-(family-name:--text) text-[14px] text-(--ink-faint)'>{t.transparencia.nombres[nombre]}</dt>
                <dd className='m-0 font-(family-name:--text) text-[15px]'>
                  {contextoEnIdioma(valor, idioma) === null ? (
                    respaldo.tipo === 'cita' && respaldo.cita !== null ? null : (
                      <span className='text-(--ink-soft)'>{t.transparencia.sinTraduccion}</span>
                    )
                  ) : (
                    <span className='text-(--ink)'>{contextoEnIdioma(valor, idioma)}</span>
                  )}
                  {respaldo.tipo === 'web' ? (
                    // ING-36: no lo dice el estudio, lo dice su web. Se nota, y se enlaza la página.
                    <a href={respaldo.url} {...ENLACE_A_EDITOR} className='ml-2 text-[13px] text-(--teal-deep)'>
                      ({t.transparencia.segunSuWeb})
                    </a>
                  ) : respaldo.tipo === 'logo' ? (
                    // Un logo no es una frase literal: se dice dónde está, sin comillas.
                    respaldo.pagina === null ? null : (
                      <span className='ml-2 text-[13px] text-(--ink-soft)'>
                        ({(respaldo.pagina === 1 ? t.transparencia.logoPortada : t.transparencia.logo).replace('{p}', String(respaldo.pagina))})
                      </span>
                    )
                  ) : respaldo.cita === null ? null : (
                    <blockquote className='mt-[6px] mb-0 border-l-2 border-(--line-strong) pl-3 text-[13px] leading-[1.5] text-(--ink-soft) italic'>
                      <q>{respaldo.cita}</q>
                      {respaldo.pagina === null ? null : (
                        <span className='ml-2 font-(family-name:--mono) text-[12px] text-(--ink-faint)'>p. {String(respaldo.pagina)}</span>
                      )}
                    </blockquote>
                  )}
                </dd>
              </div>
            ))}
          </dl>
          {/* Lo relevante que falta, en una línea discreta. «No indica» solo si alguien lo ha mirado. */}
          {lineas.length === 0 ? null : <p className='mt-4 mb-0 font-(family-name:--text) text-[13.5px] text-(--ink-faint)'>{lineas.join(' ')}</p>}
        </section>
      </main>
    </div>
  );
}
